import { z } from 'zod';
import { uuid } from './schemas.js';

/**
 * The management lists (Phase 13d-2, 06 §23): every list is searched,
 * filtered, sorted and paged on the server; the same query gives its CSV.
 */
export const PAGE_SIZES = [10, 25, 50] as const;
/** the period pill: today, the last 7, 30 or 90 days */
export const PERIOD_DAYS = [1, 7, 30, 90] as const;
/** D-103: model prices are in Turkish lira for now */
export const PRICE_CURRENCY = 'TRY';
/** the most rows one CSV export holds (D-102) */
export const CSV_MAX_ROWS = 10_000;

const q = z.string().trim().max(200).optional();
const dir = z.enum(['asc', 'desc']).optional();
const page = {
  page: z.coerce.number().int().min(1).max(100_000).default(1),
  size: z.coerce
    .number()
    .int()
    .refine((v) => (PAGE_SIZES as readonly number[]).includes(v), 'one of 10, 25, 50')
    .default(25),
};
const days = z.coerce
  .number()
  .int()
  .refine((v) => (PERIOD_DAYS as readonly number[]).includes(v), 'one of 1, 7, 30, 90')
  .default(30);
const flag = z
  .enum(['0', '1', 'true', 'false'])
  .optional()
  .transform((v) => v === '1' || v === 'true');

export const memberStatuses = ['active', 'inactive', 'deactivated'] as const;
export const roleKinds = ['owner', 'admin', 'domain_admin', 'member'] as const;

export const adminMembersQuery = z.object({
  q,
  domain: uuid.optional(),
  role: z.enum(roleKinds).optional(),
  status: z.enum(memberStatuses).optional(),
  sort: z.enum(['name', 'email', 'role', 'status', 'lastSignIn']).default('lastSignIn'),
  dir,
  ...page,
});

export const adminDomainsQuery = z.object({ q, sort: z.enum(['name', 'members']).default('name'), dir, ...page });

export const usageChips = ['full', 'near', 'custom', 'idle'] as const;

export const adminUsageMembersQuery = z.object({
  days,
  q,
  domain: uuid.optional(),
  state: z.enum(usageChips).optional(),
  // "en çok kullanan üstte": the share of today's budget, so those at their budget come first
  sort: z.enum(['today', 'percent', 'month', 'budget', 'name', 'input', 'output', 'calls', 'runs', 'sandbox', 'storage', 'cost']).default('percent'),
  dir,
  ...page,
});

export const adminUsageWorkspacesQuery = z.object({
  days,
  q,
  domain: uuid.optional(),
  kind: z.enum(['chat', 'code']).optional(),
  sort: z.enum(['input', 'output', 'calls', 'runs', 'sandbox', 'storage', 'cost', 'name']).default('input'),
  dir,
  ...page,
});

export const adminUsageSummaryQuery = z.object({ days });

export const skillScopes = ['builtin', 'organization', 'team', 'personal'] as const;

export const adminSkillsQuery = z.object({
  days,
  q,
  scope: z.enum(skillScopes).optional(),
  domain: uuid.optional(),
  sort: z.enum(['loads', 'model', 'failed', 'users', 'tokens', 'lastUsed', 'name']).default('loads'),
  dir,
  ...page,
});

/** the audit log's date filter: today, the last 7, 30, 90 days, or everything (0) */
export const AUDIT_DAYS = [1, 7, 30, 90, 0] as const;

export const auditCategories = ['access', 'members', 'budgets', 'workspaces', 'skills', 'integrations', 'policies', 'models', 'license', 'system', 'exports'] as const;
export type AuditCategory = (typeof auditCategories)[number];

/**
 * Phase 13g (D-140): the audit log's tabs, each a set of categories, with their counts; "Tümü" holds every event
 * (skills and connections only there, the Tür column names them).
 */
export const auditTabs = ['all', 'access', 'budgets', 'models', 'connections', 'license', 'system'] as const;
export type AuditTab = (typeof auditTabs)[number];
export const AUDIT_TAB_CATEGORIES: Record<Exclude<AuditTab, 'all'>, AuditCategory[]> = {
  access: ['access', 'members', 'workspaces', 'policies'],
  budgets: ['budgets'],
  models: ['models'],
  // D-181: connections and every call through them
  connections: ['integrations'],
  license: ['license'],
  system: ['system', 'exports'],
};

export const adminAuditQuery = z.object({
  q,
  category: z.enum(auditCategories).optional(),
  /** Phase 13g: a tab's categories */
  tab: z.enum(auditTabs).optional(),
  /** exact actions, comma-separated (deep links such as a skill's failed loads) */
  action: z
    .string()
    .max(500)
    .regex(/^[a-z_.]+(,[a-z_.]+)*$/)
    .optional(),
  person: uuid.optional(),
  days: z.coerce
    .number()
    .int()
    .refine((v) => (AUDIT_DAYS as readonly number[]).includes(v), 'one of 1, 7, 30, 90, 0')
    .default(7),
  warnings: flag,
  ...page,
});

/** a budget: at least a thousand tokens, null clears it (the next level applies) */
const budget = z.number().int().min(1000).max(1e11).nullable();

/** Yönetim → Kullanım ve limitler: the organization's budgets, a domain's default and members' own (single or bulk). */
export const adminQuotasBody = z
  .object({
    /** the organization's monthly token budget; null: the plan's */
    tenantMonthlyTokens: z.number().int().min(1000).max(1e12).nullable().optional(),
    /** every member's daily prompt budget; null: the platform's per user type */
    memberDailyInputTokens: budget.optional(),
    /** a domain's default daily budget for its members; null: the organization's (Phase 13d-2) */
    domains: z.array(z.object({ domainId: uuid, dailyInputTokens: budget })).max(100).optional(),
    /** one member's own daily budget; null: their domain's or the organization's */
    users: z.array(z.object({ userId: uuid, dailyInputTokens: budget })).max(500).optional(),
    /** the members were chosen together ("Günlük bütçe ata", "Varsayılana döndür"): audited as one bulk change */
    bulk: z.boolean().optional(),
  })
  .refine(
    (v) => v.tenantMonthlyTokens !== undefined || v.memberDailyInputTokens !== undefined || (v.users?.length ?? 0) > 0 || (v.domains?.length ?? 0) > 0,
    { message: 'nothing to update' },
  );

// Responses -------------------------------------------------------------------------------------------------

export interface PageDto<T> {
  rows: T[];
  total: number;
  page: number;
  size: number;
}

/** A directory domain as the lists show it: its code and its name (the display name, else the code without its prefix). */
export interface DomainRefDto {
  id: string;
  code: string;
  name: string;
  admin?: boolean;
}

export type MemberStatus = (typeof memberStatuses)[number];
export type RoleKind = (typeof roleKinds)[number];

export interface AdminMemberDto {
  userId: string;
  name: string;
  email: string;
  username: string | null;
  role: 'owner' | 'admin' | 'member';
  roleKind: RoleKind;
  operator: boolean;
  /** signs in through the company directory (the role comes from there) */
  directory: boolean;
  lastSignInAt: string | null;
  joinedAt: string;
  deactivatedAt: string | null;
  status: MemberStatus;
  domains: DomainRefDto[];
}

export interface AdminMembersDto extends PageDto<AdminMemberDto> {
  counts: Record<'all' | MemberStatus, number>;
}

export interface AdminDomainDto {
  id: string;
  code: string;
  name: string;
  members: number;
  dailyInputTokens: number | null;
  workspace: { id: string; name: string };
  admins: { id: string; name: string }[];
  skills: { id: string; name: string }[];
}

export interface SystemAccountDto {
  userId: string;
  name: string;
  email: string;
  active: boolean;
  twoFactor: boolean;
  lastUsedAt: string | null;
  createdAt: string;
}

export interface RelinkRequestDto {
  id: string;
  user: { id: string; name: string; email: string };
  /** the new directory account */
  username: string | null;
  email: string;
  attempts: number;
  createdAt: string;
  lastAttemptAt: string;
}

export type BudgetSource = 'personal' | 'domain' | 'organization' | 'platform';
export type BudgetState = 'full' | 'near' | 'ok';
export type UsageChip = (typeof usageChips)[number];

export interface UsagePeriodDto {
  input: number;
  output: number;
  calls: number;
  runs: number;
  sandboxSeconds: number;
  storageBytes: number;
  costMicros: number;
}

export interface UsageMemberDto {
  userId: string;
  name: string;
  email: string;
  userType: 'developer' | 'business' | null;
  deactivated: boolean;
  domains: DomainRefDto[];
  /** prompt tokens since midnight UTC (what the daily budget counts) */
  today: number;
  budget: number;
  source: BudgetSource;
  state: BudgetState;
  /** all tokens since the first of the month (UTC) */
  month: number;
  /** the budget without the member's own: their domain's, the organization's or the platform's */
  inherited: number;
  inheritedSource: Exclude<BudgetSource, 'personal'>;
  period: UsagePeriodDto;
}

export interface BudgetRights {
  /** the organization's budgets (owners and admins) */
  organization: boolean;
  /** the domains whose default and members the caller may change */
  domains: string[];
  /** a domain admin's limit (D-106); null: none */
  ceiling: number | null;
}

export interface UsageMembersDto extends PageDto<UsageMemberDto> {
  counts: Record<'all' | UsageChip, number>;
  days: number;
  /** when the daily budgets reset (midnight UTC) */
  resetsAt: string;
  rights: BudgetRights;
}

export interface AdminUsageSummaryDto {
  days: number;
  month: { used: number; limit: number | null };
  today: { active: number; members: number };
  full: number;
  near: number;
  period: number;
  defaults: { monthly: number | null; memberDaily: number | null; business: number; developer: number };
  domains: {
    id: string;
    code: string;
    name: string;
    members: number;
    today: number;
    period: number;
    dailyInputTokens: number | null;
    /** the members' default: the domain's, else the organization's; null: the platform's per user type */
    budget: number | null;
    source: Exclude<BudgetSource, 'personal'>;
    full: number;
    near: number;
  }[];
  rights: BudgetRights;
}

export interface UsageWorkspaceDto {
  id: string;
  name: string;
  kind: 'chat' | 'code';
  directoryManaged: boolean;
  owner: { id: string; name: string };
  domain: DomainRefDto | null;
  input: number;
  output: number;
  calls: number;
  runs: number;
  sandboxSeconds: number;
  storageBytes: number;
  costMicros: number;
}

export interface UsageWorkspacesDto extends PageDto<UsageWorkspaceDto> {
  days: number;
  totals: Omit<UsageWorkspaceDto, 'id' | 'name' | 'kind' | 'directoryManaged' | 'owner' | 'domain'>;
  /** a price is set for a model the organization can use; otherwise costs show as "—" */
  pricesSet: boolean;
  currency: string;
}

export type SkillScope = (typeof skillScopes)[number];

export interface SkillUsageRowDto {
  key: string;
  skillId: string | null;
  name: string | null;
  scope: SkillScope;
  domain: DomainRefDto | null;
  loads: number;
  byModel: number;
  byUser: number;
  failed: number;
  users: number;
  bodyTokens: number;
  lastUsed: string | null;
}

export interface SkillAnalyticsPageDto extends PageDto<SkillUsageRowDto> {
  days: number;
  stats: { loads: number; skills: number; people: number; byModel: number; failed: number };
  unused: { id: string; name: string; scope: 'organization' | 'team'; domain: DomainRefDto | null }[];
}

export interface AuditEntryDto {
  id: string;
  action: string;
  actor: { id: string; name: string | null; email: string | null } | null;
  target: { type: string; id: string; name: string | null; email: string | null } | null;
  result: string;
  metadata: Record<string, unknown>;
  createdAt: string;
  warning: boolean;
}

export interface AuditPageDto extends PageDto<AuditEntryDto> {
  warnings: number;
  /** Phase 13g: each tab's count under the other filters */
  tabs: Record<AuditTab, number>;
}

// The audit events (D-111) ------------------------------------------------------------------------------------

export type AuditIcon =
  | 'login'
  | 'logout'
  | 'alert'
  | 'people'
  | 'budget'
  | 'workspace'
  | 'skill'
  | 'link'
  | 'shield'
  | 'server'
  | 'download'
  | 'key'
  | 'model'
  | 'backup'
  | 'update'
  | 'lock'
  // Phase 16c (B2cPlatformDenetim): e-mail, Pro, a card's payments
  | 'mail'
  | 'pro'
  | 'card';

export interface AuditEventInfo {
  category: AuditCategory;
  icon: AuditIcon;
  /** shown under "Yalnız uyarılar" and highlighted */
  warning?: boolean;
  title: { tr: string; en: string };
}

const e = (category: AuditCategory, icon: AuditIcon, tr: string, en: string, warning = false): AuditEventInfo => ({
  category,
  icon,
  ...(warning ? { warning } : {}),
  title: { tr, en },
});

export const AUDIT_EVENTS: Record<string, AuditEventInfo> = {
  // sign-in and sessions
  'auth.login': e('access', 'login', 'Giriş yapıldı', 'Signed in'),
  'auth.logout': e('access', 'logout', 'Çıkış yapıldı', 'Signed out'),
  'auth.login_failed': e('access', 'alert', 'Giriş başarısız', 'Sign-in failed', true),
  'auth.refresh_refused': e('access', 'alert', 'Şirket girişi yenilenmedi, oturum kapandı', 'Company sign-in not renewed, session ended', true),
  'auth.break_glass_login': e('access', 'key', 'Acil durum yöneticisi girişi', 'Break-glass admin signed in', true),
  'auth.password_changed': e('access', 'key', 'Parola değişti', 'Password changed'),
  'auth.sessions_revoked': e('access', 'logout', 'Oturumları kapatıldı', 'Sessions ended'),
  // access and roles
  'tenant.member_role_changed': e('members', 'people', 'Üyenin rolü değişti', "Member's role changed"),
  'tenant.member_removed': e('members', 'people', 'Üye çıkarıldı', 'Member removed'),
  'tenant.member_left': e('members', 'people', 'Üye ayrıldı', 'Member left'),
  'tenant.member_deactivated': e('members', 'alert', 'Üyenin erişimi kapatıldı', 'Member’s access turned off', true),
  'tenant.member_reactivated': e('members', 'people', 'Üyenin erişimi açıldı', 'Member’s access turned on'),
  'tenant.member_data_purged': e('members', 'people', 'Ayrılan üyenin verileri silindi', 'A former member’s data deleted'),
  'tenant.invite_created': e('members', 'people', 'Davet gönderildi', 'Invitation sent'),
  'tenant.invite_accepted': e('members', 'people', 'Davet kabul edildi', 'Invitation accepted'),
  'auth.role_changed': e('members', 'people', 'Dizindeki rol değişti', 'Directory role changed'),
  'auth.domains_changed': e('members', 'people', 'Domain üyeliği değişti', 'Domain membership changed'),
  'auth.link_requested': e('members', 'link', 'Yerel hesap şirket girişine bağlanmak üzere işaretlendi', 'Local account armed to join the company sign-in'),
  'auth.account_linked': e('members', 'link', 'Yerel hesap şirket girişine bağlandı', 'Local account joined the company sign-in'),
  'auth.relink_required': e('members', 'alert', 'Şirket hesabı değişti, onay bekliyor', 'Company account changed, waiting for approval', true),
  'auth.account_relinked': e('members', 'link', 'Hesap yeni şirket hesabına bağlandı', 'Account linked to the new company account'),
  'auth.break_glass_set': e('members', 'key', 'Acil durum yöneticisi ayarlandı', 'Break-glass admin set up'),
  'user.data_deleted': e('members', 'people', 'Kullanıcı verilerini sildi', 'User deleted their data'),
  // budgets
  'quota.changed': e('budgets', 'budget', 'Bütçe değişti', 'Budget changed'),
  'quota.exceeded': e('budgets', 'alert', 'Token bütçesi doldu', 'Token budget used up', true),
  // workspaces and documents
  'workspace.created': e('workspaces', 'workspace', 'Çalışma alanı oluşturuldu', 'Workspace created'),
  'workspace.deleted': e('workspaces', 'workspace', 'Çalışma alanı silindi', 'Workspace deleted'),
  'identity.domain_renamed': e('workspaces', 'workspace', "Domain'in çalışma alanı adı değişti", 'Domain workspace renamed'),
  'identity.domain_opened': e('workspaces', 'workspace', 'Ekip açıldı', 'Team opened'),
  'document.uploaded': e('workspaces', 'workspace', 'Belge yüklendi', 'Document uploaded'),
  // Microsoft 365 part 3: one row per copied document (scope, kind, source), next to the batch's microsoft.documents_imported
  'document.imported': e('workspaces', 'workspace', 'Belge içe aktarıldı', 'Document imported'),
  'document.shared': e('workspaces', 'workspace', 'Belge paylaşıldı', 'Document shared'),
  'document.retried': e('workspaces', 'workspace', 'Belge yeniden okunuyor', 'Document read again'),
  'document.deleted': e('workspaces', 'workspace', 'Belge silindi', 'Document deleted'),
  // skills and teams
  'skill.created': e('skills', 'skill', 'Skill eklendi', 'Skill added'),
  'skill.proposed': e('skills', 'skill', 'Skill önerildi', 'Skill proposed'),
  'skill.published': e('skills', 'skill', 'Skill yayımlandı', 'Skill published'),
  'skill.rejected': e('skills', 'skill', 'Skill reddedildi', 'Skill rejected'),
  'skill.deleted': e('skills', 'skill', 'Skill silindi', 'Skill deleted'),
  'skill.rolled_back': e('skills', 'skill', 'Skill eski sürüme döndü', 'Skill rolled back'),
  'skill.version_added': e('skills', 'skill', "Skill'in yeni sürümü", 'New skill version'),
  'skill.load_failed': e('skills', 'alert', 'Skill yüklenemedi', 'Skill failed to load', true),
  'team.created': e('skills', 'people', 'Ekip oluşturuldu', 'Team created'),
  'team.deleted': e('skills', 'people', 'Ekip silindi', 'Team deleted'),
  // integrations
  'mcp.server_created': e('integrations', 'link', 'Bağlantı eklendi', 'Connection added'),
  'mcp.server_updated': e('integrations', 'link', 'Bağlantı değişti', 'Connection changed'),
  'mcp.server_deleted': e('integrations', 'link', 'Bağlantı silindi', 'Connection deleted'),
  'mcp.server_trusted': e('integrations', 'link', 'Bağlantıya güvenildi', 'Connection trusted'),
  'mcp.server_untrusted': e('integrations', 'link', 'Bağlantının güveni kaldırıldı', 'Connection no longer trusted'),
  'mcp.oauth_client_set': e('integrations', 'link', 'Bağlantının giriş istemcisi kaydedildi', "Connection's sign-in client saved"),
  'mcp.oauth_client_cleared': e('integrations', 'link', 'Bağlantının giriş istemcisi silindi', "Connection's sign-in client removed"),
  // D-181 (Phase 15d): tool policies, accounts and every tool call through a connection; never arguments or results
  'mcp.tool_policy_changed': e('integrations', 'link', 'Aracın izni değişti', "Tool's permission changed"),
  'mcp.account_connected': e('integrations', 'link', 'Hesap bağlandı', 'Account connected'),
  'mcp.account_disconnected': e('integrations', 'link', 'Hesap bağlantısı kaldırıldı', 'Account disconnected'),
  'mcp.tool_called': e('integrations', 'link', 'Bağlantının aracı kullanıldı', "Connection's tool used"),
  // Phase 15e (D-199): Microsoft 365, the built-in connection; never mail, attachment or calendar content
  'microsoft.setup_saved': e('integrations', 'key', 'Microsoft 365 kurulumu kaydedildi', 'Microsoft 365 setup saved'),
  'microsoft.setup_removed': e('integrations', 'key', 'Microsoft 365 kurulumu kaldırıldı', 'Microsoft 365 setup removed', true),
  'microsoft.tested': e('integrations', 'link', 'Microsoft bağlantısı test edildi', 'Microsoft connection tested'),
  'microsoft.settings_changed': e('integrations', 'shield', 'Microsoft 365 ayarı değişti', 'Microsoft 365 setting changed'),
  'microsoft.team_changed': e('integrations', 'people', 'Microsoft 365 bir ekip için açıldı ya da kapatıldı', 'Microsoft 365 turned on or off for a team'),
  'microsoft.account_connected': e('integrations', 'link', 'Microsoft hesabı bağlandı', 'Microsoft account connected'),
  'microsoft.account_disconnected': e('integrations', 'link', 'Microsoft bağlantısı kaldırıldı', 'Microsoft connection removed'),
  'microsoft.sign_in_failed': e('integrations', 'alert', 'Microsoft hesabı bağlanamadı', 'Microsoft account could not be connected', true),
  'microsoft.refresh_failed': e('integrations', 'alert', 'Microsoft bağlantısı yenilenemedi', 'Microsoft connection could not be renewed', true),
  'microsoft.consent_requested': e('integrations', 'shield', 'Microsoft için yönetici onayı istendi', 'Admin consent for Microsoft requested'),
  'microsoft.tool_called': e('integrations', 'link', 'Microsoft 365 aracı kullanıldı', 'Microsoft 365 tool used'),
  // Microsoft 365 part 3 (D-220…): one row per write card's end and per copy; counts only, never a subject, a text, an
  // address, a file name or a Graph id. Not sent / not created are shown as failures (audit-details FAILURES).
  'microsoft.mail_sent': e('integrations', 'link', 'Mail gönderildi (onaylı)', 'Mail sent (approved)'),
  'microsoft.mail_not_sent': e('integrations', 'alert', 'Mail gönderilmedi', 'Mail not sent'),
  'microsoft.meeting_created': e('integrations', 'link', 'Toplantı oluşturuldu (onaylı)', 'Meeting created (approved)'),
  'microsoft.meeting_not_created': e('integrations', 'alert', 'Toplantı oluşturulmadı', 'Meeting not created'),
  'microsoft.documents_imported': e('integrations', 'workspace', 'Belge alındı', 'Documents imported'),
  'microsoft.attachment_saved': e('integrations', 'workspace', 'Mail eki belgelere kaydedildi', 'Mail attachment saved to documents'),
  'microsoft.document_refetched': e('integrations', 'workspace', 'Belge yeniden alındı', 'Document fetched again'),
  // policies and settings
  'tenant.created': e('policies', 'shield', 'Firma oluşturuldu', 'Company created'),
  'tenant.converted': e('policies', 'shield', 'Kişisel alan firmaya dönüştürüldü', 'Personal space turned into a company'),
  'tenant.renamed': e('policies', 'shield', 'Firmanın adı değişti', 'Company renamed'),
  'tenant.settings_changed': e('policies', 'shield', 'Firma ayarları değişti', 'Company settings changed'),
  'tenant.full_access_changed': e('policies', 'shield', 'Tam erişim izni değişti', 'Full access permission changed'),
  'tenant.document_search_changed': e('policies', 'shield', 'Belge araması değişti', 'Document search changed'),
  // D-253: "Şimdi yeniden dene" on Belge araması: the company's waits after failed embeddings ended (counts only)
  'tenant.document_vectors_retried': e('policies', 'shield', 'Anlam dizini için yeniden denendi', 'Meaning index tried again'),
  'tenant.models_allowed_changed': e('models', 'model', 'İzin verilen modeller değişti', 'Allowed models changed'),
  'tenant.personal_providers_changed': e('models', 'model', 'Kişisel sağlayıcı izni değişti', 'Personal provider permission changed'),
  'tenant.web_policy_changed': e('policies', 'shield', 'Web politikası değişti', 'Web policy changed'),
  'approval.decided': e('policies', 'shield', 'Onay kartı yanıtlandı', 'Approval card answered'),
  'approval_mode.changed': e('policies', 'shield', 'Onay kipi değişti', 'Approval mode changed'),
  'permission_rule.revoked': e('policies', 'shield', 'İzin kuralı kaldırıldı', 'Permission rule removed'),
  // the identity provider
  'auth.provider_created': e('access', 'server', 'Şirket giriş servisi bağlandı', 'Company sign-in service connected'),
  'auth.provider_changed': e('access', 'server', 'Şirket giriş servisinin ayarları değişti', 'Company sign-in service settings changed'),
  // models: the company's (Yönetim → Modeller; Phase 13g, D-140)
  'models.provider_added': e('models', 'model', 'Sağlayıcı eklendi', 'Provider added'),
  'models.provider_updated': e('models', 'model', 'Sağlayıcı düzenlendi', 'Provider edited'),
  'models.provider_removed': e('models', 'model', 'Sağlayıcı kaldırıldı', 'Provider removed'),
  'models.provider_tested': e('models', 'model', 'Sağlayıcının bağlantısı denendi', "Provider's connection tested"),
  'models.models_refreshed': e('models', 'model', 'Modeller yenilendi', 'Models refreshed'),
  'models.model_updated': e('models', 'model', 'Modelin ayrıntıları değişti', "Model's details changed"),
  'models.task_model_changed': e('models', 'model', 'Görev modeli değişti', 'Task model changed'),
  // models: the platform operator's (bireysel Platform → Denetim kaydı)
  'platform.internal_providers_changed': e('models', 'lock', 'İç ağ adresi izni değişti', 'Internal address permission changed'),
  'platform.provider_created': e('models', 'model', 'Platform sağlayıcısı eklendi', 'Platform provider added'),
  'platform.provider_updated': e('models', 'model', 'Platform sağlayıcısı düzenlendi', 'Platform provider edited'),
  'platform.provider_deleted': e('models', 'model', 'Platform sağlayıcısı kaldırıldı', 'Platform provider removed'),
  'platform.provider_tested': e('models', 'model', 'Platform sağlayıcısının bağlantısı denendi', "Platform provider's connection tested"),
  'platform.models_refreshed': e('models', 'model', 'Platform modelleri yenilendi', 'Platform models refreshed'),
  'platform.model_updated': e('models', 'model', 'Platform modelinin ayrıntıları değişti', "Platform model's details changed"),
  'platform.route_changed': e('models', 'model', 'Platformun görev modeli değişti', "Platform's task model changed"),
  // Phase 16a (bireysel): plans, Pro by hand, fair use and the model share; counts and reasons only, never tokens of a talk
  'platform.plan_updated': e('budgets', 'budget', 'Paketin değerleri değişti', "Plan's values changed"),
  'platform.plan_change_scheduled': e('budgets', 'budget', 'Ücretsiz paketin değişikliği planlandı', "Ücretsiz plan's change scheduled"),
  'platform.plan_change_cancelled': e('budgets', 'budget', 'Paket değişikliğinden vazgeçildi', 'Plan change cancelled'),
  'platform.pro_granted': e('members', 'pro', 'Pro elle verildi', 'Pro granted by hand'),
  'platform.pro_revoked': e('members', 'pro', 'Pro geri alındı', 'Pro taken back'),
  'platform.fair_use_mail_sent': e('budgets', 'mail', 'Adil kullanım e-postası gönderildi', 'Fair use e-mail sent'),
  'platform.model_share_changed': e('models', 'model', 'Model payı değişti', 'Model share changed'),
  // Phase 16c (bireysel): the code boxes' limit, payments and invoices, a person's own account and subscription
  'platform.sandbox_cap_changed': e('models', 'server', 'Kod kutusu sınırı değişti', 'Code box limit changed'),
  'platform.payment_refunded': e('budgets', 'card', 'Ödeme iade edildi', 'Payment refunded'),
  'platform.invoice_issued': e('budgets', 'download', 'Fatura kesildi', 'Invoice issued'),
  'platform.billing_exported': e('exports', 'download', 'Ödemeler dışa aktarıldı', 'Payments exported'),
  'account.opened': e('access', 'login', 'Hesap açıldı', 'Account opened'),
  // Phase 16c.1: a person's marketing consent from Ayarlar or a mail's unsubscribe link; payments opened for those who asked
  'account.marketing_withdrawn': e('access', 'mail', 'Kampanya izni geri alındı', 'Marketing consent withdrawn'),
  'account.marketing_given': e('access', 'mail', 'Kampanya izni verildi', 'Marketing consent given'),
  'system.pro_open_mailed': e('system', 'mail', 'Pro açıldı e-postaları gönderildi', 'Pro open e-mails sent'),
  'billing.subscription_started': e('budgets', 'card', 'Abonelik başladı', 'Subscription started'),
  'billing.checkout_failed': e('budgets', 'alert', 'İlk ödeme alınamadı', 'First payment not taken', true),
  'billing.renewed': e('budgets', 'card', 'Abonelik yenilendi', 'Subscription renewed'),
  'billing.payment_failed': e('budgets', 'alert', 'Ödeme alınamadı', 'Payment not taken', true),
  'billing.subscription_ended': e('budgets', 'pro', 'Abonelik bitti', 'Subscription ended'),
  'billing.cancel_requested': e('budgets', 'pro', 'Abonelik iptal edildi', 'Subscription cancelled'),
  'billing.cancel_undone': e('budgets', 'pro', 'İptalden vazgeçildi', 'Cancellation undone'),
  'billing.period_changed': e('budgets', 'card', 'Ödeme sıklığı değişecek', 'Billing period to change'),
  'billing.card_changed': e('budgets', 'card', 'Ödeme kartı değişti', 'Payment card changed'),
  'billing.terms_applied': e('budgets', 'pro', "Pro'nun yeni değerleri başladı", "Pro's new values started"),
  // the license (Phase 13f, D-127): the installation's, in every firm's log
  'license.uploaded': e('license', 'key', 'Lisans dosyası yüklendi', 'License file uploaded'),
  'license.applied': e('license', 'key', 'Lisans uygulandı', 'License applied'),
  'license.rejected': e('license', 'alert', 'Lisans dosyası reddedildi', 'License file rejected', true),
  'license.state_changed': e('license', 'shield', 'Lisans durumu değişti', 'License status changed'),
  // the free trial (Phase 13f-2, D-132)
  'license.trial_started': e('license', 'key', 'Deneme başladı', 'Trial started'),
  'license.trial_reminder': e('license', 'key', 'Deneme hatırlatması gönderildi', 'Trial reminder sent'),
  'license.trial_ended': e('license', 'shield', 'Deneme bitti', 'Trial ended'),
  // the system (Phase 13g, D-140, D-141): backups, updates, the model server
  // Phase 16c: the title says how a backup started (auditView: "Gece yedeği alındı" for the night's, "Yedek alındı"
  // otherwise; rows from before name no start and get the plain title)
  'system.backup_done': e('system', 'backup', 'Yedek alındı', 'Backup taken'),
  'system.backup_failed': e('system', 'alert', 'Yedek alınamadı', 'Backup failed', true),
  'system.updated': e('system', 'update', 'Oppy güncellendi', 'Oppy updated'),
  'system.model_server_down': e('system', 'alert', 'Model sunucusuna ulaşılamadı', 'Model server unreachable', true),
  'system.model_server_up': e('system', 'model', 'Model sunucusuna yeniden ulaşıldı', 'Model server reachable again'),
  'system.pro_ended': e('system', 'pro', 'Elle verilen Pro bitti', 'Pro given by hand ended'),
  // Phase 16c: scripts/server/mail-test.sh's test mail
  'system.mail_test': e('system', 'mail', 'Deneme e-postası gönderildi', 'Test e-mail sent'),
  'system.mail_test_failed': e('system', 'alert', 'Deneme e-postası gönderilemedi', 'Test e-mail not sent', true),
  'system.plan_change_applied': e('system', 'budget', 'Ücretsiz paketin yeni değerleri başladı', "Ücretsiz plan's new values started"),
  'demo.seeded': e('system', 'server', 'Demo verisi yüklendi', 'Demo data added'),
  'demo.cleared': e('system', 'server', 'Demo verisi temizlendi', 'Demo data removed'),
  // exports
  'audit.exported': e('exports', 'download', 'Denetim kaydı indirildi', 'Audit log downloaded'),
  'admin.exported': e('exports', 'download', 'Liste indirildi', 'List downloaded'),
};

const FALLBACK: Omit<AuditEventInfo, 'title'> = { category: 'system', icon: 'shield' };

/** An action's title, category and icon; an unknown action keeps its code as its title. */
export function auditEventInfo(action: string): AuditEventInfo {
  return AUDIT_EVENTS[action] ?? { ...FALLBACK, title: { tr: action, en: action } };
}

export const AUDIT_WARNING_ACTIONS: string[] = Object.entries(AUDIT_EVENTS)
  .filter(([, info]) => info.warning)
  .map(([action]) => action);

export function auditActionsOf(category: AuditCategory): string[] {
  return Object.entries(AUDIT_EVENTS)
    .filter(([, info]) => info.category === category)
    .map(([action]) => action);
}

/** A tab's actions; null for "Tümü" (every action, known or not). */
export function auditActionsOfTab(tab: AuditTab): string[] | null {
  return tab === 'all' ? null : AUDIT_TAB_CATEGORIES[tab].flatMap(auditActionsOf);
}

/** Text as the lists' search compares it: lower case, Turkish letters folded (the database's app_fold). */
export function foldSearch(text: string): string {
  const map: Record<string, string> = { İ: 'i', I: 'i', ı: 'i', Ş: 's', ş: 's', Ğ: 'g', ğ: 'g', Ü: 'u', ü: 'u', Ö: 'o', ö: 'o', Ç: 'c', ç: 'c', Â: 'a', â: 'a', Î: 'i', î: 'i', Û: 'u', û: 'u' };
  return text.replace(/[İIıŞşĞğÜüÖöÇçÂâÎîÛû]/g, (c) => map[c] ?? c).toLowerCase();
}

/** The actions whose titles (Turkish or English) contain the search text. */
export function auditActionsMatching(query: string): string[] {
  const needle = foldSearch(query.trim());
  if (!needle) return [];
  return Object.entries(AUDIT_EVENTS)
    .filter(([, info]) => foldSearch(info.title.tr).includes(needle) || foldSearch(info.title.en).includes(needle))
    .map(([action]) => action);
}

// Platform › Denetim kaydı in the bireysel edition (Phase 16c, B2cPlatformDenetim) -------------------------------

/**
 * The platform log's tabs: sign-ups and access, plans (Pro by hand, the catalogue), payments, capacity (the model
 * share, the code boxes), the platform's models, e-mail, and the system. "Tümü" holds every event.
 */
export const platformAuditTabs = ['all', 'access', 'plans', 'billing', 'capacity', 'models', 'mail', 'system'] as const;
export type PlatformAuditTab = (typeof platformAuditTabs)[number];

const PLATFORM_TAB_OF: Record<string, Exclude<PlatformAuditTab, 'all'>> = {
  'account.opened': 'access',
  'platform.internal_providers_changed': 'models',
  'platform.plan_updated': 'plans',
  'platform.plan_change_scheduled': 'plans',
  'platform.plan_change_cancelled': 'plans',
  'platform.pro_granted': 'plans',
  'platform.pro_revoked': 'plans',
  'system.pro_ended': 'plans',
  'system.plan_change_applied': 'plans',
  'platform.model_share_changed': 'capacity',
  'platform.sandbox_cap_changed': 'capacity',
  'platform.payment_refunded': 'billing',
  'platform.invoice_issued': 'billing',
  'platform.billing_exported': 'billing',
  'platform.fair_use_mail_sent': 'mail',
  'system.pro_open_mailed': 'mail',
  'system.mail_test': 'mail',
  'system.mail_test_failed': 'mail',
};

/** A platform log event's tab (and its "Tür" chip) */
export function platformAuditTabOf(action: string): Exclude<PlatformAuditTab, 'all'> {
  const known = PLATFORM_TAB_OF[action];
  if (known) return known;
  if (action.startsWith('billing.')) return 'billing';
  if (action.startsWith('account.')) return 'access';
  if (/^platform\.(provider|model|route)/.test(action)) return 'models';
  return 'system';
}

/** A tab's known actions; null for "Tümü" */
export function platformAuditActionsOfTab(tab: PlatformAuditTab): string[] | null {
  return tab === 'all' ? null : Object.keys(AUDIT_EVENTS).filter((action) => platformAuditTabOf(action) === tab);
}

export const platformAuditQuery = z.object({
  q,
  tab: z.enum(platformAuditTabs).optional(),
  person: uuid.optional(),
  days: z.coerce
    .number()
    .int()
    .refine((v) => (AUDIT_DAYS as readonly number[]).includes(v), 'one of 1, 7, 30, 90, 0')
    .default(7),
  ...page,
});

export interface PlatformAuditPageDto extends PageDto<AuditEntryDto> {
  tabs: Record<PlatformAuditTab, number>;
}
